import { useDisclosure } from '@heroui/react';
import { ReactNode, useEffect, useMemo } from 'react';
import { useQueue } from '@shared/util/hooks/useQueue';
import { Alert } from './Alert';
import { Confirm } from './Confirm';
import { ModalContext } from './ModalContext';
import { ModalQueueItem, useAlertCreator, useConfirmCreator } from './useModalCreator';

export function ModalProvider({ children }: { children: ReactNode }) {
  const queue = useQueue<ModalQueueItem>();
  const alert = useAlertCreator(queue);
  const confirm = useConfirmCreator(queue);
  const value = useMemo(() => ({ alert, confirm }), [alert, confirm]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {queue.current && <ModalRenderer key={queue.current.id} modal={queue.current} />}
    </ModalContext.Provider>
  );
}

function ModalRenderer({ modal }: { modal: ModalQueueItem }) {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  const { id, type, ...modalProps } = modal;

  async function handleClose() {
    await modalProps.onClose?.();
    onClose();
  }

  useEffect(onOpen, [onOpen]);

  const Component = type === 'confirm' ? Confirm : Alert;

  return (
    <Component
      key={id}
      {...modalProps}
      isOpen={isOpen}
      onClose={handleClose}
      onOpenChange={onOpenChange}
    />
  );
}
